import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { PrivateRouteProps } from "../types/common";

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children: Children,
  requiredPermission,
  permissionKey,
  secondaryKey,
  ...rest
}) => {
  const location = useLocation();
  // return Children
  const { isAuthenticated, token, rolePermissions, user } = useSelector(
    (state: RootState) => state.auth,
  );

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  const publicPaths = ['/login', '/change-password'];

  // Normalize current pathname to ignore trailing slashes and queries
  const rawPath = location.pathname || "/";
  const currentPath = rawPath.split("?")[0].replace(/\/+$|^\s+|\s+$/g, "") || "/";

  const isPublicPath = (path: string) =>
    publicPaths.some(p => currentPath === p || currentPath.startsWith(p + "/"));

  // If the user exists and is required to change their password (force_change_password is null or false),
  // redirect them to the change-password page — but allow navigation to public paths like /login and /change-password
  // (including query params or trailing slashes).
  if (
    user &&
    // Treat any falsy value (null, false, undefined) as "must change password" so the
    // user is not considered fully logged-in for protected routes.
    !user.force_change_password &&
    !isPublicPath(currentPath)
  ) {
    // If the user's account indicates they must change password (null/false),
    // treat them as not fully logged-in for accessing post-login routes and
    // redirect to the login page. The login flow itself still navigates to
    // /change-password after initial authentication.
    return <Navigate to="/login" />;
  }

  // If user does not need to change password (force_change_password is truthy)
  // and they are on the change-password page, send them to the app root.
  if (user && user.force_change_password && (currentPath === '/change-password' || currentPath.startsWith('/change-password/'))) {
    return <Navigate to="/" />;
  }
  if (rolePermissions && permissionKey && requiredPermission) {
    const hasPermission = rolePermissions[permissionKey as keyof typeof rolePermissions]
      ? requiredPermission !== "is_available" &&
        rolePermissions[permissionKey as keyof typeof rolePermissions][requiredPermission]?.is_visible &&
        (secondaryKey
          ? rolePermissions[permissionKey as keyof typeof rolePermissions][requiredPermission]?.type === "All" ||
            (rolePermissions[permissionKey as keyof typeof rolePermissions][requiredPermission]?.type === "partial" &&
              Array.isArray(rolePermissions[permissionKey as keyof typeof rolePermissions][requiredPermission]?.partial) &&
              rolePermissions[permissionKey as keyof typeof rolePermissions][requiredPermission]?.partial.includes(secondaryKey))
          : true)
      : false;

    return token && hasPermission ? Children : <Navigate to="/not-found" />;
  } else {
    return token ? Children : <Navigate to="/unauthorized" />;
  }
};

export default PrivateRoute;